import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { updateOrAppendItem } from './helpers';

import type {
  WsCustomGroupData,
  WsFlowData,
  WsNotificationData,
  WsReportBannerData,
  WsReportStatusData,
  WsReportSurveyGptJobData,
  WsSurveyGptStatusData,
  WsSurveyValidationData,
  WsTabProgressData,
} from './types';

const initialState = {
  customGroups: [],
  flows: [],
  isConnected: false,
  notification: {
    isToast: false,
    message: '',
    type: null as null | string,
  },
  reports: [],
  surveygptJobs: [],
  surveyValidation: [],
  tabs: [],
};

const websocketSlice = createSlice({
  initialState,
  name: 'websocket',
  reducers: {
    updateCustomGroupStatus: (state, action: PayloadAction<{ data: WsCustomGroupData }>) => {
      const { data } = action.payload;

      updateOrAppendItem({
        array: state.customGroups,
        data: {
          conditionalDefinitionAttributes: data.conditional_definition_attributes,
          id: data.customgroup_id,
          status: data.customgroup_status,
        },
      });
    },
    updateFlowStatus: (state, action: PayloadAction<{ data: WsFlowData }>) => {
      const { data } = action.payload;
      updateOrAppendItem({
        array: state.flows,
        data: {
          completeFlowTopicIds: data.complete_flow_topic_ids,
          id: data.flow_id,
          incompleteFlowTopicIds: data.incomplete_flow_topic_ids,
          message: data.message,
          numberCompleteTopics: data.complete_flow_topics,
          numberIncompleteTopics: data.incomplete_flow_topics,
          status: data.flow_status,
          type: data.type,
        },
      });
    },
    updateNotificationCenter: (state, action: PayloadAction<{ data: WsNotificationData }>) => {
      const { data } = action.payload;
      state.notification.isToast = data.is_toast;
      state.notification.message = data.message;
      state.notification.type = data.type;
    },
    updateReportBanner: (state, action: PayloadAction<{ data: WsReportBannerData }>) => {
      const { data } = action.payload;

      updateOrAppendItem({
        array: state.reports,
        data: { characteristicsJobId: data.job_id, id: data.job_id },
      });
    },
    updateReportStatus: (state, action: PayloadAction<{ data: WsReportStatusData }>) => {
      const { data } = action.payload;

      updateOrAppendItem({
        array: state.reports,
        data: {
          completeJobItemIds: data.complete_job_item_ids,
          id: data.job_id,
          message: data.message,
          numberCompleteTabs: data.complete_job_items,
          numberIncompleteTabs: data.incomplete_job_items,
          status: data.job_status,
          type: data.type,
        },
      });
    },
    updateReportSurveyGptJob: (
      state,
      action: PayloadAction<{ data: WsReportSurveyGptJobData }>
    ) => {
      const { data } = action.payload;

      if (data?.job_id) {
        updateOrAppendItem({
          array: state.reports,
          data: { id: data.job_id, surveyGPTJobId: data.surveygpt_job_id },
        });
      }
    },
    updateSurveyQuestionsGptStatus: (
      state,
      action: PayloadAction<{ data: WsSurveyGptStatusData }>
    ) => {
      const { data } = action.payload;
      if (!data?.surveygpt_job_id) return;

      updateOrAppendItem({
        array: state.surveygptJobs,
        data: {
          id: data.surveygpt_job_id,
          message: data.message,
          status: data.status,
          type: data.type,
        },
      });
    },
    updateSurveyValidationStatus: (
      state,
      action: PayloadAction<{ data: WsSurveyValidationData }>
    ) => {
      const { data } = action.payload;

      updateOrAppendItem({
        array: state.surveyValidation,
        data: {
          id: data.atlas_instant_survey_id,
          status: data.status,
        },
      });
    },
    updateTabProgress: (state, action: PayloadAction<{ data: WsTabProgressData }>) => {
      const { data } = action.payload;

      updateOrAppendItem({
        array: state.tabs,
        data: {
          documentId: data.document_id,
          id: data.job_id,
          progress: data.progress,
          reportId: data.job_id,
          tabId: data.job_item_id,
          type: data.type,
        },
      });
    },
    websocketConnect: (state) => state,
    websocketConnected: (state) => {
      state.isConnected = true;
    },
    websocketDisconnect: (state) => state,
    websocketDisconnected: (state) => {
      state.isConnected = false;
    },
    websocketReset: () => initialState,
  },
});

export const {
  updateCustomGroupStatus,
  updateFlowStatus,
  updateNotificationCenter,
  updateReportBanner,
  updateReportStatus,
  updateReportSurveyGptJob,
  updateSurveyQuestionsGptStatus,
  updateSurveyValidationStatus,
  updateTabProgress,
  websocketConnect,
  websocketConnected,
  websocketDisconnect,
  websocketDisconnected,
  websocketReset,
} = websocketSlice.actions;

export default websocketSlice.reducer;
